/**
 * Otto Views API client.
 *
 * Routes through `env.STREAMING_API_URL` (the public ALB) like the Otto chat
 * client: a first-visit GET or an explicit regenerate runs one bounded LLM
 * generation server-side, which can exceed API Gateway's 30s idle timeout.
 *
 * `streamView` is the preferred generation path — same call, delivered as SSE
 * so blocks render as the model composes them. `getView`/`regenerate` remain
 * the JSON equivalents and the cache-read path.
 */

import {env} from '@/matic/config/env';
import {AuthenticatedApiClient} from '@/matic/lib/api/authenticated-api-client';
import {trackProductEvent} from '@/matic/lib/product-observability';
import type {
  BriefRefTarget,
  ViewBlock,
  ViewBlockEntry,
  ViewDefinition,
  ViewEnvelope,
  ViewInstanceSummary,
  ViewResult,
  ViewSteer,
  ViewStatus,
  ViewTemplate,
} from '../types';

const BASE_PATH = '/api/v1/agents/otto/views';

// Safety net so a generation always resolves to some terminal state. The server
// heartbeats every 15s through the byte-silent stretch while the model composes,
// so genuine silence this long means the stream died.
const VIEW_STREAM_STALL_TIMEOUT_MS = 60_000;

const VIEW_STREAM_ERROR =
  "Couldn't generate this view. The previous version (if any) is unchanged.";
const VIEW_STREAM_STALL_ERROR =
  'This view stopped composing. Try generating it again.';

/**
 * Which branch ended the stream. Several of these render the same copy to the
 * reader, so this is the only thing that tells them apart afterwards:
 *
 * - `fetch-threw` — the request never opened (offline, DNS, TLS, CORS).
 * - `not-ok` — a pre-stream 4xx/5xx; the server's own detail is shown instead.
 * - `no-body` — 200 with no readable body (should be impossible; a proxy quirk).
 * - `pump-threw` — connection severed mid-stream (truncated response).
 * - `clean-eof-no-terminal` — response closed tidily but no `complete`/`error`
 *   frame ever arrived; something in the path hung up on a live generation.
 * - `stalled` — the 60s watchdog fired: heartbeats stopped reaching us.
 * - `server-error-event` — the server sent a real `error` frame (the healthy
 *   failure path; generation itself failed).
 */
type ViewStreamFailureReason =
  | 'fetch-threw'
  | 'not-ok'
  | 'no-body'
  | 'pump-threw'
  | 'clean-eof-no-terminal'
  | 'stalled'
  | 'server-error-event';

/**
 * Report a failed generation to product observability.
 *
 * These failures are close to unreproducible on demand and the reader only
 * ever sees one sentence of copy, so the diagnosis has to be captured at the
 * moment it happens and shipped somewhere durable. This lands as a
 * `PRODUCT_EVENT` in CloudWatch, which is where the server's matching
 * `[VIEW] Stream …` line already is — the two together say whether the client
 * or the server let go first.
 */
function reportViewStreamFailure(details: {
  reason: ViewStreamFailureReason;
  elapsedMs: number;
  definitionId?: string;
  refs: string[];
  force: boolean;
  blocksReceived: number;
  status?: number;
  errorName?: string;
  errorMessage?: string;
  stats?: ViewPumpStats;
}): void {
  const {stats, refs, elapsedMs, ...rest} = details;
  trackProductEvent('ai.otto.view_stream_failed', {
    ...rest,
    elapsedMs: Math.round(elapsedMs),
    refCount: refs.length,
    refs,
    chunks: stats?.chunks,
    bytes: stats?.bytes,
    framesDispatched: stats?.framesDispatched,
    msSinceLastChunk: stats?.msSinceLastChunk,
  });
}

/** The Definition's block shape, sent before the model has produced anything —
 *  what the loading skeleton is drawn from. */
export type ViewStreamProgress = {
  scopeKey: string;
  definitionId?: string;
  definitionName?: string;
  /** Threads (record scope) or member records (portfolio) feeding the read. */
  sourceCount: number;
  blocks: Array<{
    blockId: string;
    blockType: ViewBlockEntry['blockType'];
    width: ViewBlockEntry['width'];
    title?: string;
  }>;
};

export type ViewStreamCallbacks = {
  onProgress: (progress: ViewStreamProgress) => void;
  /** One validated block plus the citation targets it introduces, ready to
   *  render. Superseded by `onComplete`. */
  onBlock: (block: ViewBlock, refs: Record<string, BriefRefTarget>) => void;
  /** The authoritative persisted View — the terminal event. */
  onComplete: (result: ViewResult) => void;
  onError: (message: string) => void;
};

/* eslint-disable @typescript-eslint/naming-convention */
type WireViewProgress = {
  scope_key: string;
  definition_id?: string | null;
  definition_name?: string | null;
  thread_count?: number;
  blocks: Array<{
    block_id: string;
    type: ViewBlockEntry['blockType'];
    width: ViewBlockEntry['width'];
    title?: string | null;
  }>;
};

type WireView = {
  scope_key: string;
  definition_id: string;
  definition_name: string;
  stale: boolean;
  generated: boolean;
  spec_version: number;
  generated_at: string;
  view: ViewEnvelope;
  refs: Record<string, BriefRefTarget>;
  steer_updated_at?: string | null;
  /** Additive — absent on responses from a build that predates per-block
   *  staleness, which is indistinguishable from "no block is stale". */
  stale_block_ids?: string[] | null;
};

type WireViewStatus = {
  scope_key: string;
  definition_id: string;
  definition_name: string;
  generated: boolean;
  stale: boolean;
  generated_at?: string | null;
  steer_updated_at?: string | null;
};

type WireViewInstanceSummary = {
  scope_key: string;
  definition_id: string;
  generated_at: string;
};

type WireViewInstanceList = {
  items: WireViewInstanceSummary[];
};

export type WireViewSteer = {
  scope_key: string;
  definition_id: string;
  instructions: string;
  updated_at?: string | null;
  updated_by?: string | null;
};

type WireViewBlockEntry = {
  id: string;
  block_type: ViewBlockEntry['blockType'];
  width: ViewBlockEntry['width'];
  title?: string | null;
  prompt: string;
};

export type WireViewDefinition = {
  id: string;
  name: string;
  blocks: WireViewBlockEntry[];
  is_builtin: boolean;
  scope_mode?: 'records' | 'type';
  origin_scope_key?: string | null;
  is_shared?: boolean;
  created_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type WireViewDefinitionList = {
  items: WireViewDefinition[];
};

type WireViewTemplate = {
  key: string;
  name: string;
  description?: string;
  scope_mode?: 'records' | 'type';
  blocks: WireViewBlockEntry[];
};

type WireViewTemplateList = {
  items: WireViewTemplate[];
};

/** Block entry in a Definition write — `id` omitted lets the server mint one. */
type WireViewBlockEntryPayload = {
  id?: string;
  block_type: ViewBlockEntry['blockType'];
  width: ViewBlockEntry['width'];
  title?: string | null;
  prompt: string;
};
/* eslint-enable @typescript-eslint/naming-convention */

function toViewStreamProgress(wire: WireViewProgress): ViewStreamProgress {
  return {
    scopeKey: wire.scope_key,
    definitionId: wire.definition_id ?? undefined,
    definitionName: wire.definition_name ?? undefined,
    sourceCount: wire.thread_count ?? 0,
    blocks: (wire.blocks ?? []).map((block) => ({
      blockId: block.block_id,
      blockType: block.type,
      width: block.width,
      title: block.title ?? undefined,
    })),
  };
}

type ViewPumpOutcome = 'ok' | 'stalled' | 'aborted';

/**
 * What the read loop saw, kept alongside the outcome for failure telemetry.
 *
 * A stream that dies mid-generation looks identical from the outcome alone —
 * a severed connection surfaces as a thrown network error, and an
 * intermediary closing the response cleanly surfaces as `done` with no
 * terminal event — so the counters are what separate them after the fact.
 * `msSinceLastChunk` is the load-bearing one: near-zero means bytes were
 * flowing right up to the death (the connection was cut), while a large value
 * means the server had gone silent (heartbeats at `HEARTBEAT_INTERVAL_SECONDS`
 * stopped arriving) well before anything gave up.
 */
type ViewPumpStats = {
  chunks: number;
  bytes: number;
  framesDispatched: number;
  msSinceLastChunk: number;
};

type ViewPumpResult = ViewPumpStats & {outcome: ViewPumpOutcome};

/**
 * Shared SSE read loop for the generation stream: pumps frames, parses each,
 * and hands `(eventType, data)` to `dispatch`. Arms a stall watchdog per chunk.
 * Mirrors `HomeRecommendationsApiClient.pumpSse` — same frame grammar, same
 * outcomes — kept local rather than shared because the two clients have no
 * common base beyond `AuthenticatedApiClient`.
 *
 * Throws on a network failure mid-stream (the caller reports it); every other
 * ending resolves to a `ViewPumpResult`.
 */
async function pumpViewSse(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  dispatch: (eventType: string, parsed: unknown) => void,
  stats: ViewPumpStats
): Promise<ViewPumpResult> {
  const decoder = new TextDecoder();
  let buffer = '';
  let stalled = false;
  let lastChunkAt = Date.now();

  const handleFrame = (frame: string): void => {
    const parsed = parseViewSseFrame(frame);
    if (parsed) {
      stats.framesDispatched += 1;
      dispatch(parsed.eventType, parsed.data);
    }
  };

  let watchdog: ReturnType<typeof setTimeout> | undefined;
  const armWatchdog = () => {
    if (watchdog) clearTimeout(watchdog);
    watchdog = setTimeout(async () => {
      stalled = true;
      try {
        await reader.cancel();
      } catch {
        // Already errored/closed — nothing to release.
      }
    }, VIEW_STREAM_STALL_TIMEOUT_MS);
  };

  try {
    armWatchdog();
    // SSE chunks must be read sequentially — each frame depends on the prior buffer.
    for (
      let chunk = await reader.read();
      !chunk.done;
      // eslint-disable-next-line no-await-in-loop
      chunk = await reader.read()
    ) {
      armWatchdog();
      lastChunkAt = Date.now();
      stats.chunks += 1;
      stats.bytes += chunk.value?.byteLength ?? 0;
      buffer += decoder.decode(chunk.value, {stream: true});
      const frames = buffer.split('\n\n');
      buffer = frames.pop() ?? '';
      for (const frame of frames) {
        if (frame.trim()) handleFrame(frame);
      }
    }

    if (buffer.trim()) handleFrame(buffer);
    stats.msSinceLastChunk = Date.now() - lastChunkAt;
    return {...stats, outcome: stalled ? 'stalled' : 'ok'};
  } catch (error: unknown) {
    stats.msSinceLastChunk = Date.now() - lastChunkAt;
    if (error instanceof DOMException && error.name === 'AbortError') {
      return {...stats, outcome: 'aborted'};
    }

    throw error;
  } finally {
    if (watchdog) clearTimeout(watchdog);
    try {
      await reader.cancel();
    } catch {
      // Ignore — the stream is already finished.
    }
  }
}

/** Parse one raw SSE frame into its event type + JSON data, or undefined. */
function parseViewSseFrame(
  raw: string
): {eventType: string; data: unknown} | undefined {
  let eventType = '';
  const dataLines: string[] = [];

  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) {
      eventType = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trim());
    }
  }

  if (!eventType || dataLines.length === 0) return undefined;

  try {
    return {eventType, data: JSON.parse(dataLines.join('\n'))};
  } catch {
    return undefined; // Malformed event — skip.
  }
}

/**
 * Extract a user-facing message from a pre-stream 4xx. Every gate the JSON
 * routes enforce (role, scope, rate limit) fires here as a real status code,
 * because the plan runs before the response opens.
 */
async function extractStreamError(response: Response): Promise<string> {
  let detail = '';
  try {
    const body = (await response.json()) as {detail?: string};
    detail = body.detail ?? '';
  } catch {
    // Non-JSON body — fall through to status-based copy.
  }

  if (detail) return detail;
  if (response.status === 429) {
    return 'Too many generations right now — try again in a few minutes.';
  }

  if (response.status === 403) return "Your role can't generate this view.";
  if (response.status === 404) return 'This view no longer exists.';
  return VIEW_STREAM_ERROR;
}

function toViewResult(wire: WireView): ViewResult {
  return {
    scopeKey: wire.scope_key,
    definitionId: wire.definition_id,
    definitionName: wire.definition_name,
    stale: wire.stale,
    generated: wire.generated,
    specVersion: wire.spec_version,
    generatedAt: wire.generated_at,
    view: wire.view,
    refs: wire.refs ?? {},
    steerUpdatedAt: wire.steer_updated_at ?? undefined,
    staleBlockIds: wire.stale_block_ids ?? [],
  };
}

function toViewStatus(wire: WireViewStatus): ViewStatus {
  return {
    scopeKey: wire.scope_key,
    definitionId: wire.definition_id,
    definitionName: wire.definition_name,
    generated: wire.generated,
    stale: wire.stale,
    generatedAt: wire.generated_at ?? undefined,
    steerUpdatedAt: wire.steer_updated_at ?? undefined,
  };
}

function toViewInstanceSummary(
  wire: WireViewInstanceSummary
): ViewInstanceSummary {
  return {
    scopeKey: wire.scope_key,
    definitionId: wire.definition_id,
    generatedAt: wire.generated_at,
  };
}

export function toViewSteer(wire: WireViewSteer): ViewSteer {
  return {
    scopeKey: wire.scope_key,
    definitionId: wire.definition_id,
    instructions: wire.instructions,
    updatedAt: wire.updated_at ?? undefined,
    updatedBy: wire.updated_by ?? undefined,
  };
}

export function toViewDefinition(wire: WireViewDefinition): ViewDefinition {
  return {
    id: wire.id,
    name: wire.name,
    blocks: wire.blocks.map((entry) => ({
      id: entry.id,
      blockType: entry.block_type,
      width: entry.width,
      title: entry.title ?? undefined,
      prompt: entry.prompt,
    })),
    isBuiltin: wire.is_builtin,
    scopeMode: wire.scope_mode ?? 'records',
    originScopeKey: wire.origin_scope_key ?? undefined,
    isShared: wire.is_shared ?? false,
    createdBy: wire.created_by ?? undefined,
    createdAt: wire.created_at ?? undefined,
    updatedAt: wire.updated_at ?? undefined,
  };
}

/** Domain-side block payload for Definition writes — `id` omitted → server-minted. */
export type ViewBlockEntryInput = Omit<ViewBlockEntry, 'id'> & {id?: string};

function fromBlockEntry(entry: ViewBlockEntryInput): WireViewBlockEntryPayload {
  return {
    ...(entry.id ? {id: entry.id} : {}),
    // eslint-disable-next-line @typescript-eslint/naming-convention
    block_type: entry.blockType,
    width: entry.width,
    ...(entry.title ? {title: entry.title} : {}),
    prompt: entry.prompt,
  };
}

function toViewTemplate(wire: WireViewTemplate): ViewTemplate {
  return {
    key: wire.key,
    name: wire.name,
    description: wire.description ?? '',
    scopeMode: wire.scope_mode ?? 'records',
    blocks: wire.blocks.map((entry) => ({
      id: entry.id,
      blockType: entry.block_type,
      width: entry.width,
      title: entry.title ?? undefined,
      prompt: entry.prompt,
    })),
  };
}

function viewQuery(refs: string[], definitionId?: string): string {
  const params = new URLSearchParams();
  for (const ref of refs) params.append('ref', ref);
  if (definitionId) params.set('definition_id', definitionId);
  return params.toString();
}

export class ViewApiClient extends AuthenticatedApiClient {
  constructor(token?: string) {
    super(token, env.STREAMING_API_URL);
  }

  /**
   * Current View for the scope (generates + persists on first visit).
   * `definitionId` omitted addresses the built-in Brief.
   */
  async getView(refs: string[], definitionId?: string): Promise<ViewResult> {
    // Generation legitimately takes 10-30s — explicitly opt out of the base
    // client's default request timeout rather than riding it.
    const wire = await this.get<WireView>(
      `${BASE_PATH}?${viewQuery(refs, definitionId)}`,
      {timeoutMs: null}
    );
    return toViewResult(wire);
  }

  /** Cheap instance status — never generates. */
  async getStatus(refs: string[], definitionId?: string): Promise<ViewStatus> {
    const wire = await this.get<WireViewStatus>(
      `${BASE_PATH}/status?${viewQuery(refs, definitionId)}`
    );
    return toViewStatus(wire);
  }

  /** Recent generated instances for catalog badges. */
  async getInstances(): Promise<ViewInstanceSummary[]> {
    const wire = await this.get<WireViewInstanceList>(`${BASE_PATH}/instances`);
    return wire.items.map((item) => toViewInstanceSummary(item));
  }

  /**
   * Generate a View over SSE, surfacing blocks as the model composes them.
   *
   * Same generation as `getView` (first visit) or `regenerate` (`force`), just
   * delivered progressively. `onComplete` carries the authoritative persisted
   * View — the same payload the JSON routes return — so the caller writes one
   * cache entry either way and can treat the streamed blocks as purely visual.
   *
   * Returns an `abort` function that cancels the underlying fetch. The
   * disconnect propagates: the server cancels the generation and skips
   * persistence (it only persists from its terminal branch), so the previously
   * persisted View is left intact — this is what makes the Stop button a true
   * revert. The tokens already spent are still billed.
   */
  streamView(
    refs: string[],
    definitionId: string | undefined,
    callbacks: ViewStreamCallbacks,
    options?: {force?: boolean}
  ): () => void {
    const controller = new AbortController();
    void this.runViewStream(
      refs,
      definitionId,
      callbacks,
      controller,
      options?.force ?? false
    );
    return () => {
      controller.abort();
    };
  }

  /** Explicit regeneration — replaces the persisted View. */
  async regenerate(refs: string[], definitionId?: string): Promise<ViewResult> {
    const wire = await this.post<WireView>(
      `${BASE_PATH}/regenerate`,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      {refs, definition_id: definitionId ?? null},
      {timeoutMs: null}
    );
    return toViewResult(wire);
  }

  /**
   * Refill ONE block against the current Definition, leaving every other
   * block's persisted content alone. Returns the MERGED artifact (the whole
   * View with just this block replaced), so the response drops into the same
   * cache entry a full regenerate writes.
   */
  async regenerateBlock(
    refs: string[],
    blockId: string,
    definitionId?: string
  ): Promise<ViewResult> {
    const wire = await this.post<WireView>(
      `${BASE_PATH}/blocks/regenerate`,
      {
        refs,
        /* eslint-disable @typescript-eslint/naming-convention */
        definition_id: definitionId ?? null,
        block_id: blockId,
        /* eslint-enable @typescript-eslint/naming-convention */
      },
      {timeoutMs: null}
    );
    return toViewResult(wire);
  }

  async getSteer(refs: string[], definitionId?: string): Promise<ViewSteer> {
    const wire = await this.get<WireViewSteer>(
      `${BASE_PATH}/steer?${viewQuery(refs, definitionId)}`
    );
    return toViewSteer(wire);
  }

  /** Saving guidance marks the cached View stale; it never regenerates by itself. */
  async updateSteer(
    refs: string[],
    instructions: string,
    definitionId?: string
  ): Promise<ViewSteer> {
    const wire = await this.put<WireViewSteer>(`${BASE_PATH}/steer`, {
      refs,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      definition_id: definitionId ?? null,
      instructions,
    });
    return toViewSteer(wire);
  }

  /**
   * The Definition gallery: built-in Brief first, then saved Definitions.
   *
   * `scope` omitted lists every Definition regardless of origin (the
   * unscoped "browse everything" catalog). When present, a custom
   * Definition is only included if it's builtin, shared, unscoped/legacy, or
   * was created in this exact scope — see `ListViewDefinitionsUseCase`.
   *
   * An aggregate scope is sent as a single `ref=type:{typeId}` token — the
   * same aggregate spelling every other Views endpoint accepts (`parse_scope`
   * treats a lone Category ref as the type-wide Portfolio path) — not a
   * separate query param the route doesn't parse.
   */
  async getDefinitions(scope?: {
    refs?: string[];
    aggregateTypeId?: string;
  }): Promise<ViewDefinition[]> {
    const refs = scope?.aggregateTypeId
      ? [`type:${scope.aggregateTypeId}`]
      : (scope?.refs ?? []);
    const params = new URLSearchParams();
    for (const ref of refs) params.append('ref', ref);

    const query = params.toString();
    const wire = await this.get<WireViewDefinitionList>(
      `${BASE_PATH}/definitions${query ? `?${query}` : ''}`
    );
    return wire.items.map((item) => toViewDefinition(item));
  }

  /** Stock starting points — applying one copies its blocks client-side. */
  async getTemplates(): Promise<ViewTemplate[]> {
    const wire = await this.get<WireViewTemplateList>(
      `${BASE_PATH}/definitions/templates`
    );
    return wire.items.map((item) => toViewTemplate(item));
  }

  /**
   * Create a tenant-owned Definition. `blocks` present = duplicate/template
   * instantiation; omitted = an empty Definition the builder fills.
   *
   * `scope` stamps the Definition's `origin_scope_key` — the scope it
   * defaults to being visible in (see `getDefinitions`). Omitted = an
   * unscoped create, visible everywhere.
   */
  async createDefinition(
    name: string,
    blocks?: ViewBlockEntryInput[],
    scopeMode?: 'records' | 'type',
    scope?: {refs?: string[]; aggregateTypeId?: string}
  ): Promise<ViewDefinition> {
    const wire = await this.post<WireViewDefinition>(
      `${BASE_PATH}/definitions`,
      {
        name,
        ...(blocks ? {blocks: blocks.map((b) => fromBlockEntry(b))} : {}),
        // Omitted defaults to 'records' server-side; only sent for a
        // type-wide (Portfolio) Definition so it lands in the type gallery.
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ...(scopeMode ? {scope_mode: scopeMode} : {}),
        ...(scope?.aggregateTypeId
          ? // eslint-disable-next-line @typescript-eslint/naming-convention
            {aggregate_type_id: scope.aggregateTypeId}
          : scope?.refs?.length
            ? {refs: scope.refs}
            : {}),
      }
    );
    return toViewDefinition(wire);
  }

  /**
   * Rename and/or wholesale-replace a Definition's blocks (last-write-wins).
   * The direct-manipulation persistence path — send the FULL block list with
   * stable ids. Editing marks dependent rendered Views stale on next read.
   */
  async updateDefinition(
    definitionId: string,
    update: {
      name?: string;
      blocks?: ViewBlockEntryInput[];
      isShared?: boolean;
    }
  ): Promise<ViewDefinition> {
    const wire = await this.patch<WireViewDefinition>(
      `${BASE_PATH}/definitions/${definitionId}`,
      {
        ...(update.name === undefined ? {} : {name: update.name}),
        ...(update.blocks
          ? {blocks: update.blocks.map((b) => fromBlockEntry(b))}
          : {}),
        ...(update.isShared === undefined
          ? {}
          : // eslint-disable-next-line @typescript-eslint/naming-convention -- snake_case wire shape
            {is_shared: update.isShared}),
      }
    );
    return toViewDefinition(wire);
  }

  /** Archive (soft-delete) a Definition. The built-in Brief 400s. */
  async archiveDefinition(definitionId: string): Promise<void> {
    await this.delete<unknown>(`${BASE_PATH}/definitions/${definitionId}`);
  }

  /**
   * Email a rendered View: `bodyHtml` renders inline as the message, `html`
   * rides along as the full attachment.
   *
   * Both are built client-side (see `buildViewEmailHtml` / `buildViewHtml`)
   * because only the browser can reproduce the live render faithfully. The
   * backend assembles the MIME message and sends via SES; `inlineImages` are
   * the chart PNGs the body references as `cid:{contentId}`, which is the only
   * form Gmail will render.
   */
  async emailView(request: {
    toEmail: string;
    subject: string;
    html: string;
    filename: string;
    backLinkUrl: string;
    definitionId?: string;
    scopeKey: string;
    bodyHtml?: string;
    inlineImages?: Array<{contentId: string; dataBase64: string}>;
  }): Promise<void> {
    await this.post<unknown>(`${BASE_PATH}/email`, {
      /* eslint-disable @typescript-eslint/naming-convention */
      to_email: request.toEmail,
      subject: request.subject,
      html: request.html,
      filename: request.filename,
      back_link_url: request.backLinkUrl,
      definition_id: request.definitionId ?? null,
      scope_key: request.scopeKey,
      body_html: request.bodyHtml ?? null,
      inline_images: (request.inlineImages ?? []).map((image) => ({
        content_id: image.contentId,
        data_base64: image.dataBase64,
      })),
      /* eslint-enable @typescript-eslint/naming-convention */
    });
  }

  private async runViewStream(
    refs: string[],
    definitionId: string | undefined,
    callbacks: ViewStreamCallbacks,
    controller: AbortController,
    force: boolean
  ): Promise<void> {
    const headers = await this.prepareHeaders({});
    headers.set('Accept', 'text/event-stream');
    headers.set('Content-Type', 'application/json');

    const startedAt = Date.now();
    const stats: ViewPumpStats = {
      chunks: 0,
      bytes: 0,
      framesDispatched: 0,
      msSinceLastChunk: 0,
    };
    let blocksReceived = 0;
    const report = (
      reason: ViewStreamFailureReason,
      extra: {status?: number; errorName?: string; errorMessage?: string} = {}
    ): void => {
      reportViewStreamFailure({
        reason,
        elapsedMs: Date.now() - startedAt,
        definitionId,
        refs,
        force,
        blocksReceived,
        stats,
        ...extra,
      });
    };

    let response: Response;
    try {
      response = await this.fetchWithAuthRetry(
        `${this.baseUrl}${BASE_PATH}/stream`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            refs,
            // eslint-disable-next-line @typescript-eslint/naming-convention -- snake_case wire shape
            definition_id: definitionId ?? null,
            // eslint-disable-next-line @typescript-eslint/naming-convention -- snake_case wire shape
            force_regenerate: force,
          }),
          signal: controller.signal,
        }
      );
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      report('fetch-threw', {
        errorName: error instanceof Error ? error.name : typeof error,
        errorMessage: error instanceof Error ? error.message : undefined,
      });
      callbacks.onError(VIEW_STREAM_ERROR);
      return;
    }

    if (!response.ok) {
      report('not-ok', {status: response.status});
      callbacks.onError(await extractStreamError(response));
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      report('no-body', {status: response.status});
      callbacks.onError(VIEW_STREAM_ERROR);
      return;
    }

    let receivedTerminal = false;
    const dispatch = (eventType: string, parsed: unknown): void => {
      switch (eventType) {
        case 'progress': {
          callbacks.onProgress(
            toViewStreamProgress(parsed as WireViewProgress)
          );
          break;
        }

        case 'block': {
          const wire = parsed as {
            block: ViewBlock;
            refs?: Record<string, BriefRefTarget>;
          };
          blocksReceived += 1;
          callbacks.onBlock(wire.block, wire.refs ?? {});
          break;
        }

        case 'complete': {
          receivedTerminal = true;
          callbacks.onComplete(toViewResult(parsed as WireView));
          break;
        }

        case 'error': {
          receivedTerminal = true;
          report('server-error-event', {
            errorMessage: (parsed as {message?: string}).message,
          });
          callbacks.onError(
            (parsed as {message?: string}).message ?? VIEW_STREAM_ERROR
          );
          break;
        }

        default:
        // Unknown event — ignore (forward compat).
      }
    };

    let result: ViewPumpResult;
    try {
      result = await pumpViewSse(reader, dispatch, stats);
    } catch (error: unknown) {
      report('pump-threw', {
        errorName: error instanceof Error ? error.name : typeof error,
        errorMessage: error instanceof Error ? error.message : undefined,
      });
      callbacks.onError(VIEW_STREAM_ERROR);
      return;
    }

    if (result.outcome === 'aborted') return;
    if (!receivedTerminal) {
      report(
        result.outcome === 'stalled' ? 'stalled' : 'clean-eof-no-terminal'
      );
      callbacks.onError(
        result.outcome === 'stalled'
          ? VIEW_STREAM_STALL_ERROR
          : VIEW_STREAM_ERROR
      );
    }
  }
}

const clientCache = new Map<string, ViewApiClient>();

export function getViewApi(token?: string): ViewApiClient {
  const key = token ?? 'no-token';
  let client = clientCache.get(key);
  if (!client) {
    client = new ViewApiClient(token);
    clientCache.set(key, client);
  }

  return client;
}
